import React from 'react';
import {
  Modal,
  ModalContent,
  ModalBody,
  Button,
  useDisclosure,
  ModalHeader,
  ModalFooter,
  ModalProps,
} from '@heroui/react';
import CloseIcon from '@assets/icons/CloseIcon';
import Text from '@components/ui/text/Text';
import { cn } from '@utils/cn';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CustomModalProps extends ModalProps {
  isOpen: boolean;
  children: React.ReactNode;
  onClose?: () => void;
  headerChildren?: React.ReactNode;
  headerChildrenClassName?: string;
  title?: string;
  buttons?: React.ReactNode;
  subtitle?: string | React.ReactNode;
  showFooterDivider?: boolean;
  showHeaderDivider?: boolean;
}

// ─── Clases del design system ─────────────────────────────────────────────────

// Página `09. Modales` del Figma. El contenedor lleva 24 de padding —el diseño
// dice 20, que no está en la escala— y la sombra proyectada del design system.
//
// `shadow-none` va **antes** a propósito: `shadow-standard` no lo conoce el
// `tailwind-merge` de HeroUI, así que su `shadow-small` sobreviviría y quedaría
// decidido por la cascada. `shadow-none` sí lo reconoce y lo descarta, dejando
// solo el nuestro. Ver `.claude/rules/tailwind.md`.
const DS_BASE_CLASSES = 'bg-surface p-6 shadow-none shadow-standard';

// El diseño dibuja el modal a 420, y ningún tamaño de HeroUI da esa medida: `sm`
// son 384 y `md` 448. Pero es el **ancho por defecto, no un tope**: los 420 valen
// para el modal de formulario del diseño, y hay contenidos que necesitan más y se
// contraen. Quien pase `size` manda.
const DS_DEFAULT_WIDTH = 'max-w-[420px]';

// **Los tres slots van a `p-0` y ponen solo su separación vertical.** HeroUI les
// da `px-6` propio, y sumado al padding del contenedor dejaba el contenido del
// cuerpo 24px más adentro que el título y los botones. Era así en los 33 modales.
const DS_HEADER_CLASSES = 'p-0';

// `gap-5` = **20px entre los bloques del cuerpo**, que es el gap del contenedor
// del modal en el diseño (`112:4311`). HeroUI trae `gap-3` (12) en este slot y
// sobrevivía al override de padding, así que **los 33 modales separaban sus
// campos por 12**. Es el gap que gobierna los formularios: cada campo del
// diseño (`43:3802`) es un bloque hermano, y sus 10px internos entre título y
// campo los pone `CustomInput`, no este contenedor.
//
// ⚠️ 20 **no está en la escala de espaciado** del design system (…16, 24…). Se
// usa igual porque es un valor medido en el propio Figma, no una invención — el
// mismo caso que el padding 20 del contenedor, que sí se redondeó a 24. Queda
// como inconsistencia a resolver con diseño.
const DS_BODY_CLASSES = 'p-0 py-6 gap-5';

// El diseño separa los botones del pie por 16 (`112:4166`). HeroUI trae `gap-2`
// (8), y solo los llamadores que envuelven los botones en su propio contenedor
// con `gap-4` salían bien; los que los pasan como fragmento quedaban en 8.
const DS_FOOTER_CLASSES = 'p-0 gap-4';

// ─── Component ────────────────────────────────────────────────────────────────

export const CustomModal: React.FC<CustomModalProps> = ({
  title,
  subtitle,
  isOpen,
  onClose,
  children,
  headerChildren,
  headerChildrenClassName,
  buttons,
  classNames,
  size,
  showFooterDivider = true,
  showHeaderDivider = true,
  ...props
}) => {
  const { onOpenChange } = useDisclosure({
    isOpen,
    onClose,
  });

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size={size ?? 'sm'}
      // `sm` en HeroUI es un radio de 8px, que es el del diseño. Antes era `md`,
      // que son 12.
      radius="sm"
      backdrop="opaque"
      placement="center"
      hideCloseButton
      classNames={{
        ...classNames,
        base: cn(DS_BASE_CLASSES, !size && DS_DEFAULT_WIDTH, classNames?.base),
        header: cn(DS_HEADER_CLASSES, classNames?.header),
        body: cn(DS_BODY_CLASSES, classNames?.body),
        footer: cn(DS_FOOTER_CLASSES, classNames?.footer),
      }}
      {...props}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader
              className={cn(
                'flex flex-col',
                showHeaderDivider
                  ? 'border-b border-neutral-40 pb-6'
                  : 'border-b-0 pb-2',
                classNames?.header,
              )}
            >
              <div
                className={cn(
                  'flex items-center justify-between',
                  headerChildrenClassName,
                )}
              >
                {/* `subtitle-1` es 16/20 Bold, el rol exacto del título del
                    modal. El peso lo trae el token. */}
                <Text fontSize="subtitle-1" textColor="text-neutral-80">
                  {title}
                </Text>
                {headerChildren ? (
                  <div className="flex">{headerChildren}</div>
                ) : (
                  <>
                    {onClose !== undefined ? (
                      <Button isIconOnly variant="light" onPress={onClose}>
                        <CloseIcon
                          className={cn('text-icon', classNames?.closeButton)}
                        />
                      </Button>
                    ) : null}
                  </>
                )}
              </div>
              {subtitle ? (
                <div className="max-w-[90%]">
                  {/* El diseño pinta el subtítulo en `text/tertiary`; el repo lo
                      tenía del mismo color que el título. */}
                  <Text fontSize="body-2" textColor="text-fg-tertiary">
                    {subtitle}
                  </Text>
                </div>
              ) : null}
            </ModalHeader>
            <ModalBody>{children}</ModalBody>
            {buttons && (
              <ModalFooter
                className={cn(
                  showFooterDivider
                    ? 'border-t border-neutral-40 pt-6'
                    : 'border-t-0 pt-4',
                  classNames?.footer,
                )}
              >
                {buttons}
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
